/* On-screen detailed report — the HTML twin of buildDetailedReportPdf's per-asset cards.
   It reads the SAME scoring helpers the PDF does (getCriterionPoints, getDowngradeNote,
   getAssetConditionText, CRITERION_VALUE/NOTE), so the web and PDF detailed reports cannot
   drift. Photos render straight from their stored URLs — no decoding needed as in the PDF. */

import React from 'react';
import { Button, Image, Tag, Typography } from 'antd';
import { BankOutlined, DownloadOutlined } from '@ant-design/icons';
import { FacilityAsset } from '../models/types';
import {
  getCriterionPoints, getDowngradeNote, getAssetConditionText, isAssetAssessed, assessedAssetCount,
  totalAssetCount, getAssetRating, getBuildingRating, maxPossibleScore, RATING_TAG,
} from '../modules/facility/facilityCalculations';
import { exportSystemCsv } from '../modules/facility/facilityCsv';
import { ReportData, SYSTEM_LABELS, SYSTEM_KEYS, CRITERION_VALUE, CRITERION_NOTE } from './reportPdf';
import { SectionTitle } from './reportChrome';

const round1 = (n: number | undefined) => Math.round((n ?? 0) * 10) / 10;

const ratingTag = (rating: string | undefined) =>
  rating ? <Tag color={RATING_TAG[rating as keyof typeof RATING_TAG]} style={{ marginInlineEnd: 0 }}>{rating}</Tag> : null;

const AssetCard: React.FC<{ asset: FacilityAsset; systemLabel: string }> = ({ asset, systemLabel }) => {
  const { rows, pinned } = getCriterionPoints(asset);
  const assessed = isAssetAssessed(asset);
  const rating = assessed ? (asset.rating ?? getAssetRating(asset.score ?? 0)) : undefined;
  const downgrade = getDowngradeNote(asset);

  return (
    <div className="report-asset report-section">
      <div className="report-asset__head">
        <span className="report-asset__title">
          {asset.assetId || '(no ID)'} — {asset.description || 'No description'}
        </span>
        <span className="report-asset__system">{systemLabel}</span>
      </div>

      {/* Identity block */}
      <table className="report-table report-table--dense report-kv">
        <tbody>
          <tr>
            <th>Location</th><td>{asset.location || '—'}</td>
            <th>Manufacturer</th><td>{asset.manufacturer || '—'}</td>
          </tr>
          <tr>
            <th>Description</th><td>{asset.description || '—'}</td>
            <th>Model / Serial</th>
            <td>{[asset.model, asset.serialNumber].filter(Boolean).join('  /  ') || '—'}</td>
          </tr>
        </tbody>
      </table>

      {/* The six condition selections, as the workbook lays them out */}
      <table className="report-table report-table--dense" style={{ marginTop: 10 }}>
        <thead>
          <tr>
            <th>Assessment Condition Selection</th><th>Selection</th>
            <th className="num">Points</th><th className="num">Max</th><th>Notes and Comments</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.key}>
              <td>{r.label}{r.band !== undefined ? ` (scored as the ${r.band} yr band)` : ''}</td>
              <td>{CRITERION_VALUE(asset, r.key)}</td>
              <td className="num report-muted">{pinned ? '—' : r.points}</td>
              <td className="num">{r.max}</td>
              <td>{CRITERION_NOTE(asset, r.key) || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Forced-downgrade banner + the "no points awarded" clarification (mirrors the PDF) */}
      {downgrade && (
        <div className="report-downgrade">
          <div className="report-downgrade__title">{downgrade}</div>
          <div className="report-downgrade__note">
            No assessment points awarded — the score below is set by this downgrade, not the criteria above.
          </div>
        </div>
      )}

      {/* Score / rating / condition sentence */}
      <table className="report-table report-table--dense report-kv report-asset__score">
        <tbody>
          <tr>
            <th>Assessment Score</th><td className="num strong">{assessed ? round1(asset.score) : '—'}</td>
            <th>Rating</th><td>{assessed ? ratingTag(rating) : <Tag>Not assessed</Tag>}</td>
            <td className="report-asset__condition">{assessed && rating ? getAssetConditionText(asset, rating) : 'Not assessed'}</td>
          </tr>
        </tbody>
      </table>

      {/* General Notes — free-text, no Excel counterpart (FacilityAsset.notes); only when set, so
          an unused field never adds blank space to the card. Mirrors the PDF's General Notes block. */}
      {asset.notes && asset.notes.trim() && (
        <div>
          <div className="report-asset__notes-label">General Notes</div>
          <div className="report-asset__notes-text">{asset.notes}</div>
        </div>
      )}

      {/* Photo evidence */}
      {(asset.photos?.length ?? 0) > 0 && (
        <div className="report-photos">
          <Image.PreviewGroup>
            {asset.photos!.map(ph => (
              <figure key={ph.id} className="report-photo">
                <Image src={ph.url} alt={ph.caption || asset.assetId} />
                {ph.caption && <figcaption>{ph.caption}</figcaption>}
              </figure>
            ))}
          </Image.PreviewGroup>
        </div>
      )}
    </div>
  );
};

const ReportDetailedView: React.FC<{ data: ReportData }> = ({ data }) => {
  if (data.facilityAssessments.length === 0) {
    return <Typography.Paragraph type="secondary">No assessment has been recorded for this project yet.</Typography.Paragraph>;
  }
  return (
    <>
      {data.facilityAssessments.map(fa => {
        const overallMax = maxPossibleScore(fa);
        const total = assessedAssetCount(fa);
        // Recorded but never reached a score/rating (isAssetAssessed needs both Visual Inspection
        // and Operational Test) — on a real walk that's almost always a unit that couldn't be
        // reached, not one the assessor forgot, so the report says so rather than leaving the gap
        // between "recorded" and "assessed" unexplained.
        const notAssessed = totalAssetCount(fa) - total;
        return (
          <div key={fa.id} style={{ marginBottom: 36 }}>
            <SectionTitle icon={<BankOutlined />}>{fa.buildingName || 'Untitled Assessment'}</SectionTitle>
            <div className="report-asset__overall">
              {overallMax > 0 ? (
                <>Overall score: <strong>{round1(fa.overallScore)} / {overallMax}</strong>{' '}
                {ratingTag(getBuildingRating(fa.overallScore ?? 0, overallMax))}</>
              ) : (
                <>Overall: {ratingTag('Not assessed')}</>
              )}
              {' · '}{total} asset{total === 1 ? '' : 's'} assessed
              {fa.assessmentDate ? ` · Assessed ${fa.assessmentDate}` : ''}
            </div>
            {notAssessed > 0 && (
              <div className="report-muted" style={{ fontSize: 12, fontStyle: 'italic', marginBottom: 10 }}>
                {notAssessed} asset{notAssessed === 1 ? '' : 's'} {notAssessed === 1 ? 'was' : 'were'} not assessed due to lack of accessibility.
              </div>
            )}
            {SYSTEM_KEYS.map(k => {
              const sys = fa[k];
              if (sys.assets.length === 0) return null;
              return (
                <div key={k} style={{ marginTop: 18 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                    <Typography.Text strong className="report-sys-head">
                      {SYSTEM_LABELS[k]} — {sys.assets.length} asset{sys.assets.length === 1 ? '' : 's'}
                    </Typography.Text>
                    <Button
                      className="no-print" size="small" icon={<DownloadOutlined />}
                      onClick={() => exportSystemCsv(fa.buildingName ?? '', k, sys.assets)}
                    >
                      Export CSV
                    </Button>
                  </div>
                  {sys.assets.map(a => <AssetCard key={a.id} asset={a} systemLabel={SYSTEM_LABELS[k]} />)}
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
};

export default ReportDetailedView;
